import classNames from "classnames";
import styles from "./AscBullpenEventCheckApplicationModal.module.scss";
import AscModal from "../AscModal/AscModal";
import AscButton from "../AscButton/AscButton";
import AscTextArea from "../AscInputFields/AscTextArea/AscTextArea";
import AscTextInput from "../AscInputFields/AscTextInput/AscTextInput";
import AscParagraph from "../AscTypography/AscParagraph/AscParagraph";
import {
  AdminGetTrackEventApplicationByIdResponse,
  normalizeFileSize,
} from "@ascendnow/shared";
import dayjs from "dayjs";
import AscHeading from "../AscTypography/AscHeading/AscHeading";
import { useEffect, useState } from "react";
import { EyeIcon, FileIcon } from "../AscIcons";
import AscFileViewModal from "../AscFileViewModal/AscFileViewModal";
import { determineFileTypeFromMimeType, formatErrorMessage } from "../../lib";
import { useAscContext } from "../../context";

export interface AscBullpenEventCheckApplicationModalProps {
  className?: string;
  visible: boolean;
  onClose: () => void;
  application: AdminGetTrackEventApplicationByIdResponse;
  onSetScore: (applicationId: string, score: number) => Promise<void>;
}

export default function AscBullpenEventCheckApplicationModal({
  className,
  visible,
  onClose,
  application,
  onSetScore,
}: AscBullpenEventCheckApplicationModalProps) {
  const [score, setScore] = useState<number>();
  const [fileIdToShow, setFileIdToShow] = useState<string>();
  const { toast } = useAscContext();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setScore(application?.results?.[0]?.score);
  }, [application?.results]);

  async function handleSubmit() {
    if (!score) {
      return;
    }
    setLoading(true);
    try {
      await onSetScore(application?.id, score);
    } catch (e) {
      toast.error(formatErrorMessage(e, "Error setting score"));
    } finally {
      setLoading(false);
    }
  }

  function handleScoreChange(event: React.ChangeEvent<HTMLInputElement>) {
    setScore(Number(event.target.value));
  }

  return (
    <AscModal
      visible={visible}
      onClose={onClose}
      data-testid="asc-bullpen-event-check-application-modal"
      modalClassName={classNames(styles.ground, className)}
      innerContentClassName={styles.innerContent}
      withBackground={false}
    >
      <AscHeading level={4}>Check application</AscHeading>
      <section className={styles.fieldsContainer}>
        <div className={styles.inputItem}>
          <AscParagraph className={styles.label}>Applicant name</AscParagraph>
          <AscTextInput disabled fullWidth value={application?.name} />
        </div>
        <div className={styles.inputItem}>
          <AscParagraph className={styles.label}>Birthday date</AscParagraph>
          <AscTextInput
            disabled
            value={dayjs(application?.birthDate).format("DD/MM/YYYY")}
            fullWidth
          />
        </div>
        <div className={styles.inputItem}>
          <AscParagraph className={styles.label}>Phone number</AscParagraph>
          <div className={styles.phoneInput}>
            <AscTextInput
              disabled
              className={styles.numberInput}
              value={application?.phone}
              fullWidth
            />
          </div>
        </div>
        <div className={styles.inputItem}>
          <AscParagraph className={styles.label}>Email</AscParagraph>
          <AscTextInput disabled fullWidth value={application?.email} />
        </div>
        <div className={styles.inputItem}>
          <AscParagraph className={styles.label}>Country</AscParagraph>
          <AscTextInput disabled value={application?.country} fullWidth />
        </div>
        <div className={styles.inputItem}>
          <AscParagraph className={styles.label}>City</AscParagraph>
          <AscTextInput disabled fullWidth value={application?.city} />
        </div>
        <div className={styles.inputItem}>
          <AscParagraph className={styles.label}>Address details</AscParagraph>
          <AscTextInput disabled fullWidth value={application?.address} />
        </div>
        <div className={styles.inputItem}>
          <AscParagraph className={styles.label}>Zip code</AscParagraph>
          <AscTextInput disabled fullWidth value={application?.zipCode} />
        </div>
        <div className={styles.inputItem}>
          <AscParagraph className={styles.label}>Project name</AscParagraph>
          <AscTextInput disabled fullWidth value={application?.projectName} />
        </div>
        <div className={styles.inputItem}>
          <AscParagraph className={styles.label}>School</AscParagraph>
          <AscTextInput disabled fullWidth value={application?.school} />
        </div>
        <div className={classNames(styles.inputItem, styles.fullWidthFlexItem)}>
          <AscParagraph className={styles.label}>
            Describe in detail what your project or idea is. What does it do?
            Provide as much detail as possible
          </AscParagraph>
          <AscTextArea
            disabled
            value={application?.description}
            fullWidth
            rows={6}
          />
        </div>
        <div className={classNames(styles.inputItem, styles.fullWidthFlexItem)}>
          <AscParagraph className={styles.label}>Files</AscParagraph>
          <ul className={styles.filesList}>
            {application?.files?.map((document) => (
              <li className={styles.fileItem} key={document?.id}>
                <FileIcon className={styles.fileIcon} />
                <div className={styles.fileTextContent}>
                  <AscParagraph className={styles.fileName}>
                    {document?.fileName}
                  </AscParagraph>
                  <AscParagraph className={styles?.fileSize}>
                    {normalizeFileSize(document?.fileSize)}
                  </AscParagraph>
                  <span></span>
                </div>
                <AscButton
                  onClick={() => setFileIdToShow(document?.id)}
                  variant="white"
                  icon={<EyeIcon />}
                />
                <AscFileViewModal
                  visible={fileIdToShow === document?.id}
                  fileName={document?.fileName}
                  fileUrl={document?.location}
                  fileType={determineFileTypeFromMimeType(document?.mimeType)}
                  onClose={() => setFileIdToShow(undefined)}
                  onError={(e) =>
                    toast.error(formatErrorMessage(e, "Error downloading file"))
                  }
                />
              </li>
            ))}
          </ul>
        </div>
      </section>
      <footer className={styles.footer}>
        <div className={styles.inputItem}>
          <AscParagraph className={styles.label}>Score</AscParagraph>
          <AscTextInput
            type="number"
            min={0}
            max={100}
            fullWidth
            value={score}
            onChange={handleScoreChange}
            placeholder="Enter score"
            disabled={loading}
          />
        </div>
        <AscButton onClick={handleSubmit} type="submit">
          Set score
        </AscButton>
      </footer>
      <AscButton
        className={styles.cancelButton}
        variant="secondary"
        type="button"
        loading={loading}
        onClick={onClose}
      >
        Close
      </AscButton>
    </AscModal>
  );
}
